export interface RequestConfig extends RequestInit {
    headers?: Record<string, string>;
    params?: Record<string, string | number | boolean | undefined>;
}

export class HttpClient {
    private baseUrl: string;
    private defaultHeaders: Record<string, string>;

    constructor(baseUrl: string = '', defaultHeaders: Record<string, string> = {}) {
        this.baseUrl = baseUrl;
        this.defaultHeaders = {
            'Content-Type': 'application/json',
            ...defaultHeaders,
        };
    }

    private async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
        const { params, headers, ...customConfig } = config;
        const url = new URL(`${this.baseUrl}${endpoint}`, typeof window !== 'undefined' ? window.location.origin : `http://localhost:${process.env.PORT || 80}`);

        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) {
                    url.searchParams.append(key, String(value));
                }
            });
        }

        const response = await fetch(url.toString(), {
            ...customConfig,
            headers: {
                ...this.defaultHeaders,
                ...headers,
            },
        });

        if (!response.ok) {
            let errorMessage = `HTTP Error: ${response.status} ${response.statusText}`;
            try {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const errorBody = await response.json();
                    errorMessage = errorBody.message || errorBody.error || JSON.stringify(errorBody);
                } else {
                    const errorText = await response.text();
                    if (errorText) errorMessage = errorText;
                }
            } catch (e) {
                // Ignore body parsing error
            }
            throw new Error(errorMessage);
        }

        // Handle empty responses
        if (response.status === 204) {
            return {} as T;
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return response.json();
        }

        return response.text() as unknown as T;
    }

    public get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
        return this.request<T>(endpoint, { ...config, method: 'GET' });
    }

    public post<T>(endpoint: string, body?: any, config?: RequestConfig): Promise<T> {
        return this.request<T>(endpoint, { ...config, method: 'POST', body: JSON.stringify(body) });
    }

    public put<T>(endpoint: string, body?: any, config?: RequestConfig): Promise<T> {
        return this.request<T>(endpoint, { ...config, method: 'PUT', body: JSON.stringify(body) });
    }

    public delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
        return this.request<T>(endpoint, { ...config, method: 'DELETE' });
    }
}
